import type { components } from '@nutians/api-client';
/**
 * EntryChip — renders one persisted meal entry as an interactive chip.
 *
 * Layout: food name / qty+unit / kcal / confidence dot (icon+value, never
 * color-only — 05-ux-standards.md). AI-generated content has a distinct
 * background + AI icon (ADR-0003 §6). Inline edit expands on tap/click.
 *
 * WCAG 2.1 AA: every interactive element is keyboard-navigable; inputs and
 * actions carry aria-labels; confidence is expressed with both icon and text.
 *
 * ADR-0013 §8.3: entries with foodId === null (unresolved / user_stated /
 * user_direct) show direct macro inputs and PATCH { directMacros }. DB-backed
 * entries show quantity + unit and let the server rescale macros.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';

type MealEntryView = components['schemas']['MealEntryView'];
type DaySummary = components['schemas']['DaySummary'];

// ---- Confidence helpers (labels resolved via i18n) --------------------------

const BAND_META: Record<
  MealEntryView['band'],
  { icon: string; dotClass: string; labelKey: string }
> = {
  auto_accept: {
    icon: '✓',
    labelKey: 'entry.band.auto_accept',
    dotClass: 'bg-leaf/100',
  },
  review: {
    icon: '!',
    labelKey: 'entry.band.review',
    dotClass: 'bg-acorn/100',
  },
  clarify: {
    icon: '?',
    labelKey: 'entry.band.clarify',
    dotClass: 'bg-clay/100',
  },
};

// ---- Component props --------------------------------------------------------

interface EntryChipProps {
  entry: MealEntryView;
  /** Called after any mutation resolves so the parent can reconcile day totals. */
  onDayUpdate?: (day: DaySummary) => void;
  /** Called when delete is triggered; parent shows undo toast. */
  onDeleteRequest?: (entry: MealEntryView, restoreFn: () => void) => void;
  /** Called when user requests a merge for a duplicate_merge note; parent owns the logic. */
  onMergeRequest?: (entry: MealEntryView) => void;
}

// ---- Inline edit mini-form --------------------------------------------------

export interface EditFormProps {
  entry: MealEntryView;
  /** Whether this entry has no catalogue backing — enables macro override. */
  canOverride: boolean;
  onSaveQty: (qty: number | null, unit: string | null) => void;
  onSaveMacros: (macros: {
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    fiberG: number;
  }) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function EditForm({
  entry,
  canOverride,
  onSaveQty,
  onSaveMacros,
  onCancel,
  isSaving,
}: EditFormProps) {
  const { t } = useTranslation('log');

  // No-food entries default to macro override mode; DB-backed entries go straight to qty.
  const [overrideMode, setOverrideMode] = useState(canOverride);

  const [qty, setQty] = useState<string>(
    entry.displayQuantity !== null && entry.displayQuantity !== undefined
      ? String(entry.displayQuantity)
      : '',
  );
  const [unit, setUnit] = useState<string>(entry.displayUnit ?? '');

  const [dKcal, setDKcal] = useState(String(Math.round(entry.macros.kcal)));
  const [dProtein, setDProtein] = useState(String(Math.round(entry.macros.proteinG)));
  const [dCarbs, setDCarbs] = useState(String(Math.round(entry.macros.carbsG)));
  const [dFat, setDFat] = useState(String(Math.round(entry.macros.fatG)));
  const [dFiber, setDFiber] = useState(String(Math.round(entry.macros.fiberG)));
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (overrideMode && canOverride) {
      const kcal = parseFloat(dKcal);
      const proteinG = parseFloat(dProtein);
      const carbsG = parseFloat(dCarbs);
      const fatG = parseFloat(dFat);
      const fiberG = parseFloat(dFiber);
      if ([kcal, proteinG, carbsG, fatG, fiberG].some((v) => Number.isNaN(v) || v < 0)) {
        setError(t('entry.errors.invalidMacros'));
        return;
      }
      onSaveMacros({ kcal, proteinG, carbsG, fatG, fiberG });
    } else {
      const parsedQty = qty.trim() !== '' ? Number(qty) : null;
      onSaveQty(parsedQty, unit.trim() || null);
    }
  }

  const macroFields = [
    { key: 'kcal' as const, value: dKcal, onChange: setDKcal },
    { key: 'proteinG' as const, value: dProtein, onChange: setDProtein },
    { key: 'carbsG' as const, value: dCarbs, onChange: setDCarbs },
    { key: 'fatG' as const, value: dFat, onChange: setDFat },
    { key: 'fiberG' as const, value: dFiber, onChange: setDFiber },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 flex flex-col gap-2"
      aria-label={`Edit entry: ${entry.name}`}
    >
      {/* Quantity + unit — shown for DB-backed entries or when override is toggled off */}
      {!overrideMode && (
        <div className="flex gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={`qty-${entry.id}`} className="text-xs text-ink-soft dark:text-ink-soft">
              {t('entry.quantity')}
            </label>
            <input
              id={`qty-${entry.id}`}
              type="number"
              step="any"
              min="0"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-24 rounded border border-ink/20 px-2 py-1 text-sm
                         focus:outline-none focus:ring-2 focus:ring-leaf
                         dark:border-ink/20 dark:bg-shell dark:text-ink"
              placeholder="amount"
              aria-label={t('entry.quantity')}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={`unit-${entry.id}`}
              className="text-xs text-ink-soft dark:text-ink-soft"
            >
              {t('entry.unit')}
            </label>
            <input
              id={`unit-${entry.id}`}
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-20 rounded border border-ink/20 px-2 py-1 text-sm
                         focus:outline-none focus:ring-2 focus:ring-leaf
                         dark:border-ink/20 dark:bg-shell dark:text-ink"
              placeholder="g, ml…"
              aria-label={t('entry.unit')}
            />
          </div>
        </div>
      )}

      {/* Direct macro inputs — shown for no-food entries in override mode (ADR-0013 §8.3) */}
      {overrideMode && canOverride && (
        <div className="flex flex-col gap-2">
          {macroFields.map(({ key, value, onChange }) => (
            <div key={key} className="flex flex-col gap-1">
              <label
                htmlFor={`macro-${key}-${entry.id}`}
                className="text-xs text-ink-soft dark:text-ink-soft"
              >
                {t(`entry.macroLabels.${key}`)}
              </label>
              <input
                id={`macro-${key}-${entry.id}`}
                type="number"
                step="any"
                min="0"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-28 rounded border border-ink/20 px-2 py-1 text-sm
                           focus:outline-none focus:ring-2 focus:ring-leaf
                           dark:border-ink/20 dark:bg-shell dark:text-ink"
                aria-label={t(`entry.macroLabels.${key}`)}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => setOverrideMode(false)}
            disabled={isSaving}
            className="self-start text-xs text-ink-soft hover:text-ink
                       focus:outline-none focus:underline disabled:opacity-50"
          >
            {t('entry.backToQuantity')}
          </button>
        </div>
      )}

      {/* "Override manually" link — for no-food entries when showing the qty form */}
      {canOverride && !overrideMode && (
        <button
          type="button"
          onClick={() => setOverrideMode(true)}
          disabled={isSaving}
          className="self-start text-xs text-leaf hover:underline
                     focus:outline-none focus:underline disabled:opacity-50"
        >
          {t('entry.overrideManually')}
        </button>
      )}

      {error && (
        <p role="alert" aria-live="polite" className="text-xs text-clay dark:text-clay">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSaving}
          className="rounded px-3 py-1 text-sm font-medium bg-leaf text-white
                     hover:bg-leaf-deep disabled:opacity-50 focus:outline-none
                     focus:ring-2 focus:ring-leaf
                     dark:bg-husk dark:text-ink dark:hover:bg-leaf-deep
                     min-h-[44px] min-w-[44px]"
        >
          {isSaving ? t('entry.saving') : t('entry.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="rounded px-3 py-1 text-sm font-medium text-ink-soft
                     hover:text-ink disabled:opacity-50 focus:outline-none
                     focus:ring-2 focus:ring-leaf
                     dark:text-ink-soft dark:hover:text-ink
                     min-h-[44px] min-w-[44px]"
        >
          {t('entry.cancel')}
        </button>
      </div>
    </form>
  );
}

// ---- Main chip --------------------------------------------------------------

export function EntryChip({ entry, onDayUpdate, onDeleteRequest, onMergeRequest }: EntryChipProps) {
  const { t } = useTranslation('log');
  const [isEditing, setIsEditing] = useState(false);
  const [dismissedDuplicate, setDismissedDuplicate] = useState(false);
  const queryClient = useQueryClient();

  const bandMeta = BAND_META[entry.band];
  const bandLabel = t(bandMeta.labelKey);

  // No-food entries (unresolved / AI-estimated / user_stated) support macro override.
  const canOverride = entry.foodId === null;
  // Invite to fix when the entry has no food and no calories yet.
  const isUnresolved = canOverride && entry.macros.kcal === 0;

  // PATCH mutation — supports both qty/unit and directMacros payloads.
  const patchMutation = useMutation({
    mutationFn: async (payload: {
      displayQuantity?: number | null;
      displayUnit?: string | null;
      directMacros?: {
        kcal: number;
        proteinG: number;
        carbsG: number;
        fatG: number;
        fiberG: number;
      };
    }) => {
      const { data, error } = await api.PATCH('/me/entries/{id}', {
        params: { path: { id: entry.id } },
        body: payload,
      });
      if (error || !data) throw new Error('Failed to update entry');
      return data;
    },
    onSuccess: (data) => {
      setIsEditing(false);
      onDayUpdate?.(data.day);
      const date = new Date(entry.recordedAt).toISOString().slice(0, 10);
      queryClient.invalidateQueries({ queryKey: ['days', date] });
    },
  });

  // DELETE mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE('/me/entries/{id}', {
        params: { path: { id: entry.id } },
      });
      if (error || !data) throw new Error('Failed to delete entry');
      return data;
    },
    onSuccess: (data) => {
      onDayUpdate?.(data.day);
      const date = new Date(entry.recordedAt).toISOString().slice(0, 10);
      queryClient.invalidateQueries({ queryKey: ['days', date] });

      const restoreFn = async () => {
        const { data: restored, error } = await api.POST('/me/entries/{id}/restore', {
          params: { path: { id: entry.id } },
        });
        if (!error && restored) {
          onDayUpdate?.(restored.day);
          queryClient.invalidateQueries({ queryKey: ['days', date] });
        }
      };

      onDeleteRequest?.({ ...entry, deletedAt: new Date().toISOString() }, restoreFn);
    },
  });

  const isMutating = patchMutation.isPending || deleteMutation.isPending;

  if (entry.deletedAt !== null) return null;

  return (
    <article
      className={`
        rounded-lg border px-4 py-3 text-sm
        ${isMutating ? 'opacity-60' : ''}
        border-husk dark:border-ink/20
        bg-shell/60 dark:bg-leaf/60
      `}
      aria-label={`Meal entry: ${entry.name}`}
    >
      {/* AI-generated content marker (05-ux-standards.md) */}
      <div className="mb-2 flex items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                     text-[10px] font-medium text-ink-soft dark:text-ink-soft
                     bg-husk dark:bg-shell"
          role="img"
          aria-label="AI-generated content"
        >
          <svg aria-hidden="true" className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 0l1.2 4.8L12 6l-4.8 1.2L6 12 4.8 7.2 0 6l4.8-1.2z" />
          </svg>
          AI
        </span>

        {/* Confidence dot — icon + text, never color-only (a11y requirement) */}
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                      text-[10px] font-medium text-ink-soft dark:text-ink-soft
                      bg-husk dark:bg-shell`}
          title={bandLabel}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${bandMeta.dotClass}`}
            aria-hidden="true"
          />
          <span aria-hidden="true">{bandMeta.icon}</span>
          <span className="sr-only">{bandLabel}</span>
          <span aria-hidden="true" className="text-[9px]">
            {Math.round(entry.confidence * 100)}%
          </span>
        </span>
      </div>

      {/* Main row: name / portion / kcal */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-medium text-ink dark:text-ink truncate">{entry.name}</span>
          {(entry.displayQuantity !== null || entry.displayUnit !== null) && (
            <span className="text-xs text-ink-soft dark:text-ink-soft">
              {entry.displayQuantity !== null ? entry.displayQuantity : ''}
              {entry.displayUnit ? ` ${entry.displayUnit}` : ''}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-medium text-ink dark:text-ink">
            {Math.round(entry.macros.kcal)} kcal
          </span>

          {/* Edit button — shows "Add calories" invite when entry is unresolved at 0 kcal */}
          <button
            type="button"
            onClick={() => setIsEditing((v) => !v)}
            disabled={isMutating}
            aria-expanded={isEditing}
            aria-label={isEditing ? 'Collapse edit form' : `Edit ${entry.name}`}
            className={`rounded focus:outline-none focus:ring-2 focus:ring-leaf
                        dark:hover:text-ink min-h-[44px] min-w-[44px] flex items-center
                        justify-center gap-1
                        ${
                          isUnresolved
                            ? 'px-2 py-1 text-xs font-medium text-leaf hover:text-leaf-deep border border-leaf/40 hover:border-leaf'
                            : 'p-1 text-ink-soft hover:text-ink'
                        }`}
          >
            {isUnresolved ? (
              t('entry.addCalories')
            ) : (
              <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.3 1.3a1 1 0 011.4 1.4L4 11.4l-2 .6.6-2 8.7-8.7zM1 14h14" />
              </svg>
            )}
          </button>

          {/* Delete button */}
          <button
            type="button"
            onClick={() => deleteMutation.mutate()}
            disabled={isMutating}
            aria-label={`Delete ${entry.name}`}
            className="rounded p-1 text-ink-soft hover:text-clay
                       focus:outline-none focus:ring-2 focus:ring-leaf
                       dark:hover:text-clay
                       min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5 1h6M2 4h12M3 4l1 10h8L13 4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Validation notes (flag/block) */}
      {entry.validationNotes && entry.validationNotes.length > 0 && (
        <ul className="mt-2 space-y-1" aria-label="Validation notes">
          {entry.validationNotes.map((note) => {
            // Duplicate-merge note gets actionable buttons; dismiss hides it client-side.
            if (note.code === 'duplicate_merge') {
              if (dismissedDuplicate) return null;
              return (
                <li
                  key={note.code}
                  className="text-xs rounded px-2 py-2 bg-acorn/10 text-acorn
                             dark:bg-acorn/10 dark:text-acorn"
                  role="alert"
                  aria-live="polite"
                >
                  <p className="mb-2">{note.message}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setDismissedDuplicate(true)}
                      className="rounded border border-acorn/40 px-2 py-1 text-xs
                                 hover:bg-acorn/20 focus:outline-none focus:ring-2
                                 focus:ring-leaf min-h-[36px]"
                    >
                      {t('entry.duplicate.keepSeparate')}
                    </button>
                    {onMergeRequest && (
                      <button
                        type="button"
                        onClick={() => onMergeRequest(entry)}
                        className="rounded border border-acorn/40 px-2 py-1 text-xs
                                   hover:bg-acorn/20 focus:outline-none focus:ring-2
                                   focus:ring-leaf min-h-[36px]"
                      >
                        {t('entry.duplicate.merge')}
                      </button>
                    )}
                  </div>
                </li>
              );
            }
            return (
              <li
                key={note.code}
                className={`text-xs rounded px-2 py-1 ${
                  note.severity === 'block'
                    ? 'bg-clay/10 text-clay dark:bg-clay/10 dark:text-clay'
                    : 'bg-acorn/10 text-acorn dark:bg-acorn/10 dark:text-acorn'
                }`}
                role="alert"
                aria-live="polite"
              >
                {note.message}
              </li>
            );
          })}
        </ul>
      )}

      {/* Inline edit form */}
      {isEditing && (
        <EditForm
          entry={entry}
          canOverride={canOverride}
          onSaveQty={(qty, unit) => {
            patchMutation.mutate({ displayQuantity: qty, displayUnit: unit });
          }}
          onSaveMacros={(macros) => {
            patchMutation.mutate({ directMacros: macros });
          }}
          onCancel={() => setIsEditing(false)}
          isSaving={patchMutation.isPending}
        />
      )}

      {/* Patch error */}
      {patchMutation.isError && (
        <p role="alert" aria-live="assertive" className="mt-1 text-xs text-clay dark:text-clay">
          {t('entry.errors.generic')}
        </p>
      )}
    </article>
  );
}
